import { useQueries, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  hotelQueryOptions,
  userGetCurrentUserOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type { HotelBean } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import {
  rateScrapeMetadataActionCachedShoppingMetricsOptions,
  hotelSettingQueryOptions,
  hotelSettingQueryShoppingOptions,
  rateScrapeMetadataQueryOptions,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type {
  HotelSettingsBean,
  HotelSiteShoppingMetricsBean,
  RateScrapeMetadataBean,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { HoverTooltip } from '../components/HoverTooltip';
import { MultiSelectCombobox } from '../components/MultiSelectCombobox';
import { PaginatedTable, type PaginatedTableColumn } from '../components/PaginatedTable';
import { TimeBasedStatus, type TimeBasedStatusEntry } from '../components/TimeBasedStatus';

type UnwrapArray<T> = T extends (infer U)[] ? U : T;

// One row per hotel shopping site; shopMetrics is null when cachedShoppingMetrics has no entry for that hotel-site.
type ActiveHotelRow = {
  hotelSettings: HotelSettingsBean;
  site: UnwrapArray<HotelSettingsBean['shoppingSites']> | null;
  shopMetrics: HotelSiteShoppingMetricsBean | null;
};

function InfoIcon({ tooltip }: { tooltip: string }): ReactElement {
  return (
    <HoverTooltip content={tooltip} className="ml-1 align-middle" placement="bottom">
      <span
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-slate-400 text-[10px] font-semibold text-slate-500"
        aria-label={tooltip}
      >
        i
      </span>
    </HoverTooltip>
  );
}

type DashboardHotelShopStat = {
  'event.externalId': string;
  status: string;
  totalSavedRates: string;
  totalSoldOut: string;
  primaryContext: string;
  additionalContext: string;
  latestRun: string;
  mostRecentError: string;
  statusCount: string;
  dryRun: '0' | '1';
};

type DashboardEnqueueStat = {
  '@timestamp': string | null;
  enqueueType: string | null;
  jobType: string | null;
  duration: string | null;
  numHotels: string | null;
  rawHotels: string | null;
};

type DashboardHotelLearningStat = {
  '@timestamp': string | null;
  'event.externalId': string;
  status: string;
  exception: string | null;
  'results.didAppendLearn': string | null;
  'results.learnedBookingEngineInfo': string | null;
  'results.learnedSelectors': string | null;
  'results.learnedCodeToLabelSelectors': string | null;
  'results.learnedLocalHotelId': string | null;
  'results.noSelectorLearning': string | null;
  'results.learningLockHeld': string | null;
  dryRun: '0' | '1';
  trace: string | null;
};

const LEARN_RATE_SHOP_RESULT_FIELD_PREFIX = 'results.';

type DashboardTab = 'ACTIVE_HOTELS' | 'RATE_SHOPS' | 'LEARN_RATE_SHOPS' | 'ENQUEUES';
const ENQUEUE_TYPE_OPTIONS = [
  'rateShoppingEnqueue',
  'learnRateShoppingEnqueue',
  'retry',
  'appendLearn',
] as const;
type EnqueueType = (typeof ENQUEUE_TYPE_OPTIONS)[number];
const JOB_TYPE_OPTIONS = ['rateShop', 'learnRateShop'] as const;
type JobType = (typeof JOB_TYPE_OPTIONS)[number];
const TAB_PARAM = 'tab';
const DATE_PARAM = 'date';
const HOTEL_ID_PARAM = 'hotelId';
const ACTIVE_HOTELS_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_ACTIVE_HOTELS_PAGE_SIZE = 25;
const SHOP_METRICS_BATCH_SIZE = 25;

const dashboardLinks: ReadonlyArray<{ label: string; href: string }> = [
  {
    label: 'Last 24 hour rate shops',
    href: 'https://us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#dashboards/dashboard/rate-shop-executions?start=PT24H&end=null',
  },
];

function buildHotelMapLink(hotelId: string): string {
  const searchParams = new URLSearchParams({
    selectedId: hotelId,
    searchMode: 'HOTEL',
    drawerTab: 'HOTEL',
  });

  return `/tools/hotel-explorer?${searchParams.toString()}`;
}

function renderHotelLink(args: {
  hotelName: string | undefined;
  primaryId: string;
  linkHotelId: string;
  isBootstrapLearner?: boolean;
  isSetsLearning?: boolean;
  isRemoteCdpHeaded?: boolean;
  shopLambdaSize?: 'NORMAL' | 'MEDIUM' | 'LARGE';
  // Overrides the value shown in the first parenthetical block (defaults to primaryId).
  externalIdLabel?: string;
  // Rendered as a second parenthetical block, e.g. "(brandName, sourceBookingEngine)".
  secondaryDetails?: string;
}): ReactElement {
  const {
    hotelName,
    primaryId,
    linkHotelId,
    isBootstrapLearner = false,
    isSetsLearning = false,
    isRemoteCdpHeaded = false,
    shopLambdaSize,
    externalIdLabel,
    secondaryDetails,
  } = args;

  const shopLambdaSizeLabel = shopLambdaSize ?? 'normal';
  const shopLambdaSizeIconCount =
    shopLambdaSize === 'LARGE' ? 3 : shopLambdaSize === 'MEDIUM' ? 2 : 1;

  return (
    <>
      <a
        className="text-sky-700 underline decoration-sky-300 underline-offset-2 transition hover:text-sky-900"
        href={buildHotelMapLink(linkHotelId)}
      >
        {hotelName ?? primaryId}
      </a>
      {hotelName && (
        <span className="ml-1 text-slate-500">
          ({externalIdLabel ?? primaryId}
          {isBootstrapLearner ? (
            <span className="ml-1" title="Bootstrap Learner" aria-label="Bootstrap Learner">
              🥾
            </span>
          ) : null}
          {isSetsLearning ? (
            <span className="ml-1" title="Sets Learning" aria-label="Sets Learning">
              🎾
            </span>
          ) : null}
          {isRemoteCdpHeaded ? (
            <span className="ml-1" title="Headed: REMOTE_CDP" aria-label="Headed: REMOTE_CDP">
              🖥️
            </span>
          ) : null}
          <span className="ml-1" title={shopLambdaSizeLabel} aria-label={shopLambdaSizeLabel}>
            {'👕'.repeat(shopLambdaSizeIconCount)}
          </span>
          )
        </span>
      )}
      {secondaryDetails && <span className="ml-1 text-slate-500">({secondaryDetails})</span>}
    </>
  );
}

function isRemoteCdpHeadedLearnExtra(learnExtra: unknown): boolean {
  if (!learnExtra || typeof learnExtra !== 'object') {
    return false;
  }

  const cssSelectors = (learnExtra as { cssSelectors?: unknown }).cssSelectors;
  if (!cssSelectors || typeof cssSelectors !== 'object') {
    return false;
  }

  const browser = (cssSelectors as { browser?: unknown }).browser;
  if (!browser || typeof browser !== 'object') {
    return false;
  }

  return (browser as { headed?: unknown }).headed === 'REMOTE_CDP';
}

type HotelRateScrapeDisplayData = {
  rateScrapeMetadataByExternalId: Map<string, RateScrapeMetadataBean>;
  hotelIdByExternalId: Map<string, string>;
  hotelByHotelId: Map<string, HotelBean>;
  hotelSettingsByHotelId: Map<string, HotelSettingsBean>;
};

// Shared by Active Hotels, Rate Shops, and Learn Rate Shops to resolve raw rate scrape metadata, hotels, and hotel settings for a set of externalIds (SITE:id format). Consumers derive display values (name, source booking engine, bootstrap learner, etc.) directly from these maps.
function useHotelRateScrapeDisplayData(args: {
  externalIds: string[];
  enabled: boolean;
  // When true, externalIds with no rate scrape metadata but a BRAND site fall back to looking up the hotel directly, since a BRAND id is the internal hotelId.
  fallbackToHotelIdFromExternalId?: boolean;
}): HotelRateScrapeDisplayData {
  const { externalIds, enabled, fallbackToHotelIdFromExternalId = false } = args;

  const { data: rateScrapeMetadataQueryResponse } = useQuery({
    ...rateScrapeMetadataQueryOptions({
      query: { externalIds },
    }),
    enabled: enabled && externalIds.length > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const { rateScrapeMetadataByExternalId, hotelIds } = useMemo(() => {
    const metadataByExternalId = new Map<string, RateScrapeMetadataBean>();
    const ids = new Set<string>();

    (rateScrapeMetadataQueryResponse?.results ?? []).forEach((metadata) => {
      const hotelId = metadata?.hotelId;

      if (typeof hotelId !== 'string') {
        return;
      }

      ids.add(hotelId);

      if (metadata.externalId) {
        metadataByExternalId.set(`${metadata.externalId.site}:${metadata.externalId.id}`, metadata);
      }
    });

    return { rateScrapeMetadataByExternalId: metadataByExternalId, hotelIds: [...ids] };
  }, [rateScrapeMetadataQueryResponse]);

  const fallbackHotelIdByExternalId = useMemo(() => {
    const map = new Map<string, string>();

    if (!fallbackToHotelIdFromExternalId) {
      return map;
    }

    externalIds.forEach((externalId) => {
      if (rateScrapeMetadataByExternalId.has(externalId)) {
        return;
      }

      const separatorIndex = externalId.indexOf(':');

      if (separatorIndex <= 0) {
        return;
      }

      const site = externalId.slice(0, separatorIndex);
      const id = externalId.slice(separatorIndex + 1);

      if (site === 'BRAND' && id) {
        map.set(externalId, id);
      }
    });

    return map;
  }, [externalIds, rateScrapeMetadataByExternalId, fallbackToHotelIdFromExternalId]);

  const hotelIdByExternalId = useMemo(() => {
    const map = new Map<string, string>();

    rateScrapeMetadataByExternalId.forEach((metadata, externalId) => {
      if (typeof metadata.hotelId === 'string') {
        map.set(externalId, metadata.hotelId);
      }
    });

    fallbackHotelIdByExternalId.forEach((hotelId, externalId) => {
      map.set(externalId, hotelId);
    });

    return map;
  }, [rateScrapeMetadataByExternalId, fallbackHotelIdByExternalId]);

  const hotelIdsToFetch = useMemo(
    () => [...new Set([...hotelIds, ...fallbackHotelIdByExternalId.values()])],
    [hotelIds, fallbackHotelIdByExternalId]
  );

  const { data: hotelsQueryResponse } = useQuery({
    ...hotelQueryOptions({
      query: { ids: hotelIdsToFetch },
    }),
    enabled: enabled && hotelIdsToFetch.length > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const hotelByHotelId = useMemo(() => {
    const map = new Map<string, HotelBean>();

    (hotelsQueryResponse?.results ?? []).forEach((hotel) => {
      if (hotel && typeof hotel.id === 'string') {
        map.set(hotel.id, hotel);
      }
    });

    return map;
  }, [hotelsQueryResponse]);

  const { data: hotelSettingsQueryResponse } = useQuery({
    ...hotelSettingQueryOptions({
      query: { ids: hotelIdsToFetch },
    }),
    enabled: enabled && hotelIdsToFetch.length > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const hotelSettingsByHotelId = useMemo(() => {
    const map = new Map<string, HotelSettingsBean>();

    (hotelSettingsQueryResponse?.results ?? []).forEach((hotelSettings) => {
      if (hotelSettings.id) {
        map.set(hotelSettings.id, hotelSettings);
      }
    });

    return map;
  }, [hotelSettingsQueryResponse]);

  return {
    rateScrapeMetadataByExternalId,
    hotelIdByExternalId,
    hotelByHotelId,
    hotelSettingsByHotelId,
  };
}

function formatLatestRunInBrowserLocale(latestRunUtc: string): string {
  const isoUtc = latestRunUtc.includes('T')
    ? latestRunUtc.endsWith('Z')
      ? latestRunUtc
      : `${latestRunUtc}Z`
    : `${latestRunUtc.replace(' ', 'T')}Z`;
  const parsedUtcDate = new Date(isoUtc);

  if (Number.isNaN(parsedUtcDate.getTime())) {
    return latestRunUtc;
  }

  return parsedUtcDate.toLocaleString();
}

function formatDateAsUtcTimestamp(date: Date): string {
  return date.toISOString().slice(0, 19);
}

function buildSentryDateRange(
  selectedDate: string
): { start: string; end: string; utc: 'true' } | null {
  const [year, month, day] = selectedDate.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  const startOfDayInBrowserTimezone = new Date(year, month - 1, day, 0, 0, 0, 0);
  const endOfDayInBrowserTimezone = new Date(year, month - 1, day, 23, 59, 59, 999);

  return {
    start: formatDateAsUtcTimestamp(startOfDayInBrowserTimezone),
    end: formatDateAsUtcTimestamp(endOfDayInBrowserTimezone),
    utc: 'true',
  };
}

function buildHotelSentryLogsLink(externalId: string, selectedDate: string): string {
  const globalFilter = {
    dataset: 'logs',
    tag: {
      key: 'event.externalId',
      name: 'event.externalId',
      kind: 'tag',
    },
    value: `event.externalId:${externalId}`,
  };

  const searchParams = new URLSearchParams({
    globalFilter: JSON.stringify(globalFilter),
    release: '',
  });

  const dateRange = buildSentryDateRange(selectedDate);

  if (dateRange) {
    searchParams.set('start', dateRange.start);
    searchParams.set('end', dateRange.end);
    searchParams.set('utc', dateRange.utc);
  }

  return `https://quoruum.sentry.io/dashboard/4107132/?${searchParams.toString()}`;
}

function buildHotelShopsApiUrl(selectedDate: string, hotelId: string): string {
  const url = new URL('/api/dashboard/hotel_shops', window.location.origin);

  if (selectedDate) {
    const [year, month, day] = selectedDate.split('-').map(Number);

    if (year && month && day) {
      // These Date instances are created in the browser's local timezone.
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0);
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

      url.searchParams.set('start', startOfDay.toISOString());
      url.searchParams.set('end', endOfDay.toISOString());
    }
  }

  const trimmedHotelId = hotelId.trim();
  if (trimmedHotelId) {
    url.searchParams.set('hotelId', trimmedHotelId);
  }

  return `${url.pathname}${url.search}`;
}

function buildHotelLearningsApiUrl(selectedDate: string, hotelId: string): string {
  const url = new URL('/api/dashboard/hotel_learnings', window.location.origin);

  if (selectedDate) {
    const [year, month, day] = selectedDate.split('-').map(Number);

    if (year && month && day) {
      // These Date instances are created in the browser's local timezone.
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0);
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

      url.searchParams.set('start', startOfDay.toISOString());
      url.searchParams.set('end', endOfDay.toISOString());
    }
  }

  const trimmedHotelId = hotelId.trim();
  if (trimmedHotelId) {
    url.searchParams.set('hotelId', trimmedHotelId);
  }

  return `${url.pathname}${url.search}`;
}

function renderLearnRateShopResult(row: DashboardHotelLearningStat): ReactElement | string {
  if (row.status === 'success') {
    const resultEntries = Object.entries(row).filter(
      (entry): entry is [string, string] =>
        entry[0].startsWith(LEARN_RATE_SHOP_RESULT_FIELD_PREFIX) &&
        typeof entry[1] === 'string' &&
        entry[1].length > 0
    );

    if (resultEntries.length === 0) {
      return '-';
    }

    return (
      <ul className="list-inside list-disc space-y-1">
        {resultEntries.map(([field, value]) => (
          <li key={field}>
            {field.slice(LEARN_RATE_SHOP_RESULT_FIELD_PREFIX.length)}: {value}
          </li>
        ))}
      </ul>
    );
  }

  return row.exception ?? '-';
}

function buildEnqueuesApiUrl(
  selectedDate: string,
  selectedEnqueueTypes: ReadonlyArray<EnqueueType>,
  selectedJobTypes: ReadonlyArray<JobType>,
  hotelId: string
): string {
  const url = new URL('/api/dashboard/enqueues', window.location.origin);

  if (selectedDate) {
    const [year, month, day] = selectedDate.split('-').map(Number);

    if (year && month && day) {
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0);
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

      url.searchParams.set('start', startOfDay.toISOString());
      url.searchParams.set('end', endOfDay.toISOString());
    }
  }

  selectedEnqueueTypes.forEach((enqueueType) => {
    url.searchParams.append('enqueueType', enqueueType);
  });

  selectedJobTypes.forEach((jobType) => {
    url.searchParams.append('jobType', jobType);
  });

  const trimmedHotelId = hotelId.trim();
  if (trimmedHotelId) {
    url.searchParams.set('hotelId', trimmedHotelId);
  }

  return `${url.pathname}${url.search}`;
}

function parseRawEnqueueHotelExternalIds(rawHotels: string | null | undefined): string[] {
  if (!rawHotels) {
    return [];
  }

  return rawHotels
    .split(',')
    .map((rawHotelIdentifier) => rawHotelIdentifier.trim().replace(/^['"]|['"]$/g, ''))
    .filter((hotelIdentifier) => hotelIdentifier.length > 0)
    .map((hotelIdentifier) => {
      const separatorIndex = hotelIdentifier.lastIndexOf('.');

      if (separatorIndex <= 0 || separatorIndex >= hotelIdentifier.length - 1) {
        return hotelIdentifier;
      }

      const hotelId = hotelIdentifier.slice(0, separatorIndex);
      const site = hotelIdentifier.slice(separatorIndex + 1);

      return `${site}:${hotelId}`;
    });
}

function parseActiveTab(searchParams: URLSearchParams): DashboardTab {
  const value = searchParams.get(TAB_PARAM)?.trim().toLowerCase();

  if (value === 'enqueues') {
    return 'ENQUEUES';
  }

  if (value === 'rate-shops') {
    return 'RATE_SHOPS';
  }

  if (value === 'learn-rate-shops') {
    return 'LEARN_RATE_SHOPS';
  }

  return 'ACTIVE_HOTELS';
}

function formatTabForUrl(activeTab: DashboardTab): string {
  if (activeTab === 'ENQUEUES') {
    return 'enqueues';
  }

  if (activeTab === 'RATE_SHOPS') {
    return 'rate-shops';
  }

  if (activeTab === 'LEARN_RATE_SHOPS') {
    return 'learn-rate-shops';
  }

  return 'active-hotels';
}

function parseSelectedDate(searchParams: URLSearchParams): string {
  const value = searchParams.get(DATE_PARAM)?.trim();

  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  return '';
}

function parseHotelId(searchParams: URLSearchParams): string {
  return searchParams.get(HOTEL_ID_PARAM)?.trim() ?? '';
}

export function DashboardPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<DashboardTab>(() => parseActiveTab(searchParams));
  const [selectedDate, setSelectedDate] = useState(() => parseSelectedDate(searchParams));
  const [hotelIdFromUrl] = useState(() => parseHotelId(searchParams));
  const [selectedEnqueueTypes, setSelectedEnqueueTypes] = useState<EnqueueType[]>([
    ...ENQUEUE_TYPE_OPTIONS,
  ]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>([...JOB_TYPE_OPTIONS]);
  const [enqueueHotelIdFilter, setEnqueueHotelIdFilter] = useState(hotelIdFromUrl);
  const [rateShopsHotelIdFilter, setRateShopsHotelIdFilter] = useState(hotelIdFromUrl);
  const [learnRateShopsHotelIdFilter, setLearnRateShopsHotelIdFilter] = useState(hotelIdFromUrl);
  const [hoveredHotelId, setHoveredHotelId] = useState<string | null>(null);
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);

  useEffect(() => {
    const urlTab = parseActiveTab(searchParams);
    setActiveTab((previous) => (previous === urlTab ? previous : urlTab));
  }, [searchParams]);

  useEffect(() => {
    const hotelId = parseHotelId(searchParams);

    if (activeTab === 'ENQUEUES') {
      setEnqueueHotelIdFilter((previous) => (previous === hotelId ? previous : hotelId));
    } else if (activeTab === 'RATE_SHOPS') {
      setRateShopsHotelIdFilter((previous) => (previous === hotelId ? previous : hotelId));
    } else if (activeTab === 'LEARN_RATE_SHOPS') {
      setLearnRateShopsHotelIdFilter((previous) => (previous === hotelId ? previous : hotelId));
    }
  }, [activeTab, searchParams]);

  const activeHotelIdFilter =
    activeTab === 'ENQUEUES'
      ? enqueueHotelIdFilter
      : activeTab === 'RATE_SHOPS'
        ? rateShopsHotelIdFilter
        : activeTab === 'LEARN_RATE_SHOPS'
          ? learnRateShopsHotelIdFilter
          : null;

  // Tab switches are pushed to the URL directly by handleTabChange (below) so
  // back/forward navigation works. This effect only owns the date/hotelId
  // params, and replaces the current entry so typing doesn't spam history.
  useEffect(() => {
    setSearchParams(
      (previousParams) => {
        const nextParams = new URLSearchParams(previousParams);

        if (selectedDate) {
          nextParams.set(DATE_PARAM, selectedDate);
        } else {
          nextParams.delete(DATE_PARAM);
        }

        if (activeHotelIdFilter !== null) {
          const trimmedHotelIdFilter = activeHotelIdFilter.trim();
          if (trimmedHotelIdFilter) {
            nextParams.set(HOTEL_ID_PARAM, trimmedHotelIdFilter);
          } else {
            nextParams.delete(HOTEL_ID_PARAM);
          }
        }

        return nextParams;
      },
      { replace: true }
    );
  }, [selectedDate, activeHotelIdFilter, setSearchParams]);

  function handleTabChange(nextTab: DashboardTab): void {
    if (nextTab === activeTab) {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set(TAB_PARAM, formatTabForUrl(nextTab));
      return nextParams;
    });
  }

  const { data } = useQuery({
    ...userGetCurrentUserOptions(),
  });

  const [activeHotelsPage, setActiveHotelsPage] = useState(1);
  const [activeHotelsPageSize, setActiveHotelsPageSize] = useState<number>(
    DEFAULT_ACTIVE_HOTELS_PAGE_SIZE
  );

  const {
    data: activeHotelsQueryResponse,
    isPending: isActiveHotelsPending,
    isError: isActiveHotelsError,
    error: activeHotelsError,
    isFetching: isActiveHotelsFetching,
  } = useQuery({
    ...hotelSettingQueryShoppingOptions({
      query: {
        page: activeHotelsPage,
        pageSize: activeHotelsPageSize,
      },
    }),
    enabled: activeTab === 'ACTIVE_HOTELS',
    placeholderData: (previousData) => previousData,
  });

  const activeHotelsRows = useMemo(
    () => activeHotelsQueryResponse?.results ?? [],
    [activeHotelsQueryResponse?.results]
  );

  const activeHotelsHasNextPage = typeof activeHotelsQueryResponse?.nextPage === 'number';

  const activeHotelIds = useMemo(
    () =>
      [...new Set(activeHotelsRows.map((hotelSettings) => hotelSettings.id))].filter(
        (hotelId): hotelId is string => typeof hotelId === 'string' && hotelId.length > 0
      ),
    [activeHotelsRows]
  );

  const activeHotelExternalIds = useMemo(
    () => [
      ...new Set(
        activeHotelsRows.flatMap((hotelSettings) => {
          if (!hotelSettings.id) {
            return [];
          }

          return (hotelSettings.shoppingSites ?? []).map(
            (shoppingSite) => `${shoppingSite}:${hotelSettings.id}`
          );
        })
      ),
    ],
    [activeHotelsRows]
  );

  const { data: activeHotelsHotelsQueryResponse, isFetching: isActiveHotelsHotelsFetching } =
    useQuery({
      ...hotelQueryOptions({
        query: {
          ids: activeHotelIds,
        },
      }),
      enabled: activeTab === 'ACTIVE_HOTELS' && activeHotelIds.length > 0,
      placeholderData: (previousData) => previousData,
    });

  const activeHotelExternalIdBatches = useMemo(() => {
    const batches: string[][] = [];

    for (let index = 0; index < activeHotelExternalIds.length; index += SHOP_METRICS_BATCH_SIZE) {
      batches.push(activeHotelExternalIds.slice(index, index + SHOP_METRICS_BATCH_SIZE));
    }

    return batches;
  }, [activeHotelExternalIds]);

  const activeHotelsShopMetricsQueries = useQueries({
    queries: activeHotelExternalIdBatches.map((externalIdBatch) => ({
      ...rateScrapeMetadataActionCachedShoppingMetricsOptions({
        query: {
          externalId: externalIdBatch,
        },
      }),
      enabled: activeTab === 'ACTIVE_HOTELS' && externalIdBatch.length > 0,
      placeholderData: (previousData: HotelSiteShoppingMetricsBean[] | undefined) => previousData,
    })),
  });

  const activeHotelsShopMetricsResponse = useMemo(
    () => activeHotelsShopMetricsQueries.flatMap((queryResult) => queryResult.data ?? []),
    [activeHotelsShopMetricsQueries]
  );

  const isActiveHotelsShopMetricsFetching = activeHotelsShopMetricsQueries.some(
    (queryResult) => queryResult.isFetching
  );

  const activeHotelsNameByHotelId = useMemo(() => {
    const nameByHotelId = new Map<string, string>();

    (activeHotelsHotelsQueryResponse?.results ?? []).forEach((hotel) => {
      if (typeof hotel.id === 'string' && typeof hotel.name === 'string') {
        nameByHotelId.set(hotel.id, hotel.name);
      }
    });

    return nameByHotelId;
  }, [activeHotelsHotelsQueryResponse?.results]);

  const activeHotelsBrandNameByHotelId = useMemo(() => {
    const brandNameByHotelId = new Map<string, string>();

    (activeHotelsHotelsQueryResponse?.results ?? []).forEach((hotel) => {
      if (typeof hotel.id === 'string' && typeof hotel.brand?.name === 'string') {
        brandNameByHotelId.set(hotel.id, hotel.brand.name);
      }
    });

    return brandNameByHotelId;
  }, [activeHotelsHotelsQueryResponse?.results]);

  const { rateScrapeMetadataByExternalId: activeHotelsRateScrapeMetadataByExternalId } =
    useHotelRateScrapeDisplayData({
      externalIds: activeHotelExternalIds,
      enabled: activeTab === 'ACTIVE_HOTELS',
    });

  // Metrics are one-per-hotel-site, so key by "site:hotelId" to detect sites missing from cachedShoppingMetrics.
  const activeHotelsShopMetricsBySiteHotelId = useMemo(() => {
    const shopMetricsBySiteHotelId = new Map<string, HotelSiteShoppingMetricsBean>();

    (activeHotelsShopMetricsResponse ?? []).forEach((shopMetrics) => {
      if (typeof shopMetrics.hotelId !== 'string' || !shopMetrics.site) {
        return;
      }

      shopMetricsBySiteHotelId.set(`${shopMetrics.site}:${shopMetrics.hotelId}`, shopMetrics);
    });

    return shopMetricsBySiteHotelId;
  }, [activeHotelsShopMetricsResponse]);

  // Each row represents a single shopping site for a hotel; shopMetrics is null when cachedShoppingMetrics has no entry for that hotel-site.
  const activeHotelsTableRows = useMemo<ActiveHotelRow[]>(() => {
    const rows: ActiveHotelRow[] = [];

    activeHotelsRows.forEach((hotelSettings) => {
      const hotelId = hotelSettings.id;
      const shoppingSites = hotelSettings.shoppingSites ?? [];

      if (!hotelId || shoppingSites.length === 0) {
        rows.push({ hotelSettings, site: null, shopMetrics: null });
        return;
      }

      shoppingSites.forEach((site) => {
        const metrics = activeHotelsShopMetricsBySiteHotelId.get(`${site}:${hotelId}`) ?? null;
        rows.push({ hotelSettings, site, shopMetrics: metrics });
      });
    });

    return rows;
  }, [activeHotelsRows, activeHotelsShopMetricsBySiteHotelId]);

  const activeHotelsColumns = useMemo<ReadonlyArray<PaginatedTableColumn<ActiveHotelRow>>>(
    () => [
      {
        key: 'hotel',
        header: 'Hotel',
        renderCell: ({ hotelSettings, site }) => {
          const hotelId = hotelSettings.id;

          if (!hotelId) {
            return '-';
          }

          if (isActiveHotelsHotelsFetching && !activeHotelsNameByHotelId.has(hotelId)) {
            return 'Loading...';
          }

          const hotelName = activeHotelsNameByHotelId.get(hotelId);
          const brandName = activeHotelsBrandNameByHotelId.get(hotelId) ?? '-';

          if (!site) {
            return renderHotelLink({
              hotelName,
              primaryId: hotelId,
              linkHotelId: hotelId,
            });
          }

          const externalId = `${site}:${hotelId}`;
          const metadata = activeHotelsRateScrapeMetadataByExternalId.get(externalId);
          const sourceBookingEngine = metadata?.sourceBookingEngine ?? '-';

          return renderHotelLink({
            hotelName,
            primaryId: hotelId,
            linkHotelId: hotelId,
            isBootstrapLearner: hotelSettings.optionsTuples?.some(
              (option) => option.option === 'BOOTSTRAP_LEARNING' && option.site === site
            ),
            isSetsLearning: hotelSettings.optionsTuples?.some(
              (option) => option.option === 'SETS_LEARNING' && option.site === site
            ),
            isRemoteCdpHeaded: isRemoteCdpHeadedLearnExtra(metadata?.learnExtra),
            shopLambdaSize: metadata?.shopLambdaSize,
            externalIdLabel: `${externalId}`,
            secondaryDetails: `${brandName}, ${sourceBookingEngine}`,
          });
        },
      },
      {
        key: 'status',
        header: (
          <span className="inline-flex items-center">
            Count Status
            <InfoIcon tooltip="Total number of rate shops per day. Should be 74." />
          </span>
        ),
        renderCell: ({ hotelSettings, site, shopMetrics }) => {
          const hotelId = hotelSettings.id;

          if (!hotelId) {
            return '-';
          }

          if (
            isActiveHotelsShopMetricsFetching &&
            site &&
            !activeHotelsShopMetricsBySiteHotelId.has(`${site}:${hotelId}`)
          ) {
            return 'Loading...';
          }

          if (!shopMetrics) {
            return 'No Data';
          }

          const entries: TimeBasedStatusEntry[] = (shopMetrics.asOfDayCounts ?? [])
            .filter((dayCounts) => dayCounts.asOfDay && dayCounts.countStatus)
            .map((dayCounts) => ({
              date: dayCounts.asOfDay as string,
              color: dayCounts.countStatus as TimeBasedStatusEntry['color'],
              info: `numShops: ${dayCounts.numShops ?? '-'}`,
            }));

          return (
            <div className="inline-block" title={site ?? undefined}>
              <TimeBasedStatus entries={entries} />
            </div>
          );
        },
      },
      {
        key: 'withRatesStatus',
        header: (
          <span className="inline-flex items-center">
            Rate Status
            <InfoIcon tooltip="Total number of shops that resulted in rates being saved (excludes sold out days)" />
          </span>
        ),
        renderCell: ({ hotelSettings, site, shopMetrics }) => {
          const hotelId = hotelSettings.id;

          if (!hotelId) {
            return '-';
          }

          if (
            isActiveHotelsShopMetricsFetching &&
            site &&
            !activeHotelsShopMetricsBySiteHotelId.has(`${site}:${hotelId}`)
          ) {
            return 'Loading...';
          }

          if (!shopMetrics) {
            return 'No Data';
          }

          const entries: TimeBasedStatusEntry[] = (shopMetrics.asOfDayCounts ?? [])
            .filter((dayCounts) => dayCounts.asOfDay && dayCounts.withRatesStatus)
            .map((dayCounts) => ({
              date: dayCounts.asOfDay as string,
              color: dayCounts.withRatesStatus as TimeBasedStatusEntry['color'],
              info: `numShops: ${dayCounts.numShops ?? '-'}\nnumWithRates: ${dayCounts.numWithRates ?? '-'}\nnumSoldOut: ${dayCounts.numSoldOut ?? '-'}\nnumAppendLearn: ${dayCounts.numAppendLearn ?? '-'}\nnumAppendLearnBlocked: ${dayCounts.numAppendLearnBlocked ?? '-'}\nnumErrors: ${dayCounts.numErrors ?? '-'}`,
            }));

          return (
            <div className="inline-block" title={site ?? undefined}>
              <TimeBasedStatus entries={entries} />
            </div>
          );
        },
      },
      {
        key: 'retryStatus',
        header: (
          <span className="inline-flex items-center">
            Retry Status
            <InfoIcon tooltip="The number of retries that occurred during shopping" />
          </span>
        ),
        renderCell: ({ hotelSettings, site, shopMetrics }) => {
          const hotelId = hotelSettings.id;

          if (!hotelId) {
            return '-';
          }

          if (
            isActiveHotelsShopMetricsFetching &&
            site &&
            !activeHotelsShopMetricsBySiteHotelId.has(`${site}:${hotelId}`)
          ) {
            return 'Loading...';
          }

          if (!shopMetrics) {
            return 'No Data';
          }

          const entries: TimeBasedStatusEntry[] = (shopMetrics.asOfDayCounts ?? [])
            .filter((dayCounts) => dayCounts.asOfDay && dayCounts.retryStatus)
            .map((dayCounts) => ({
              date: dayCounts.asOfDay as string,
              color: dayCounts.retryStatus as TimeBasedStatusEntry['color'],
              info: `totalRetries: ${dayCounts.totalRetries ?? '-'}\nnumMaxRetries: ${dayCounts.numMaxRetries ?? '-'}`,
            }));

          return (
            <div className="inline-block" title={site ?? undefined}>
              <TimeBasedStatus entries={entries} />
            </div>
          );
        },
      },
    ],
    [
      activeHotelsNameByHotelId,
      activeHotelsBrandNameByHotelId,
      activeHotelsRateScrapeMetadataByExternalId,
      activeHotelsShopMetricsBySiteHotelId,
      isActiveHotelsHotelsFetching,
      isActiveHotelsShopMetricsFetching,
    ]
  );

  const {
    data: hotelShopStats,
    isPending: isHotelShopStatsPending,
    isError: isHotelShopStatsError,
  } = useQuery({
    queryKey: ['dashboard-hotel-shops', selectedDate, rateShopsHotelIdFilter.trim()],
    enabled: activeTab === 'RATE_SHOPS',
    queryFn: async (): Promise<Array<DashboardHotelShopStat>> => {
      const response = await fetch(buildHotelShopsApiUrl(selectedDate, rateShopsHotelIdFilter));

      if (!response.ok) {
        throw new Error(`Failed to load hotel shop stats (${response.status}).`);
      }

      const stats = (await response.json()) as Array<DashboardHotelShopStat>;

      return [...stats].sort((a, b) => b.latestRun.localeCompare(a.latestRun));
    },
  });

  const externalIds = useMemo(() => {
    if (!hotelShopStats) {
      return [] as string[];
    }

    return [...new Set(hotelShopStats.map((row) => row['event.externalId']))];
  }, [hotelShopStats]);

  const {
    rateScrapeMetadataByExternalId,
    hotelIdByExternalId,
    hotelByHotelId,
    hotelSettingsByHotelId,
  } = useHotelRateScrapeDisplayData({
    externalIds,
    enabled: activeTab === 'RATE_SHOPS',
  });

  const {
    data: hotelLearningStats,
    isPending: isHotelLearningStatsPending,
    isError: isHotelLearningStatsError,
  } = useQuery({
    queryKey: ['dashboard-hotel-learnings', selectedDate, learnRateShopsHotelIdFilter.trim()],
    enabled: activeTab === 'LEARN_RATE_SHOPS',
    queryFn: async (): Promise<Array<DashboardHotelLearningStat>> => {
      const response = await fetch(
        buildHotelLearningsApiUrl(selectedDate, learnRateShopsHotelIdFilter)
      );

      if (!response.ok) {
        throw new Error(`Failed to load hotel learning stats (${response.status}).`);
      }

      const stats = (await response.json()) as Array<DashboardHotelLearningStat>;

      return [...stats].sort((a, b) =>
        (b['@timestamp'] ?? '').localeCompare(a['@timestamp'] ?? '')
      );
    },
  });

  const learnRateShopsExternalIds = useMemo(() => {
    if (!hotelLearningStats) {
      return [] as string[];
    }

    return [...new Set(hotelLearningStats.map((row) => row['event.externalId']))];
  }, [hotelLearningStats]);

  const {
    rateScrapeMetadataByExternalId: learnRateShopsRateScrapeMetadataByExternalId,
    hotelIdByExternalId: learnRateShopsHotelIdByExternalId,
    hotelByHotelId: learnRateShopsHotelByHotelId,
    hotelSettingsByHotelId: learnRateShopsHotelSettingsByHotelId,
  } = useHotelRateScrapeDisplayData({
    externalIds: learnRateShopsExternalIds,
    enabled: activeTab === 'LEARN_RATE_SHOPS',
    fallbackToHotelIdFromExternalId: true,
  });

  const {
    data: enqueueStats,
    isPending: isEnqueueStatsPending,
    isError: isEnqueueStatsError,
  } = useQuery({
    queryKey: [
      'dashboard-enqueues',
      selectedDate,
      selectedEnqueueTypes.join(','),
      selectedJobTypes.join(','),
      enqueueHotelIdFilter.trim(),
    ],
    queryFn: async (): Promise<Array<DashboardEnqueueStat>> => {
      const response = await fetch(
        buildEnqueuesApiUrl(
          selectedDate,
          selectedEnqueueTypes,
          selectedJobTypes,
          enqueueHotelIdFilter
        )
      );

      if (!response.ok) {
        throw new Error(`Failed to load enqueues (${response.status}).`);
      }

      const stats = (await response.json()) as Array<DashboardEnqueueStat>;
      return [...stats].sort((a, b) =>
        (b['@timestamp'] ?? '').localeCompare(a['@timestamp'] ?? '')
      );
    },
    enabled: activeTab === 'ENQUEUES',
  });

  const enqueueExternalIds = useMemo(() => {
    const ids = (enqueueStats ?? []).flatMap((row) =>
      parseRawEnqueueHotelExternalIds(row.rawHotels)
    );
    return [...new Set(ids)];
  }, [enqueueStats]);

  const { data: enqueueRateScrapeMetadataQueryResponse } = useQuery({
    ...rateScrapeMetadataQueryOptions({
      query: {
        externalIds: enqueueExternalIds,
      },
    }),
    enabled: activeTab === 'ENQUEUES' && enqueueExternalIds.length > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const enqueueHotelIds = useMemo(() => {
    const ids = (enqueueRateScrapeMetadataQueryResponse?.results ?? [])
      .map((metadata) => metadata?.hotelId)
      .filter((hotelId): hotelId is string => typeof hotelId === 'string' && hotelId.length > 0);

    return [...new Set(ids)];
  }, [enqueueRateScrapeMetadataQueryResponse]);

  const { data: enqueueHotelsQueryResponse } = useQuery({
    ...hotelQueryOptions({
      query: {
        ids: enqueueHotelIds,
      },
    }),
    enabled: activeTab === 'ENQUEUES' && enqueueHotelIds.length > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const {
    enqueueHotelNameByHotelId,
    enqueueHotelIdByExternalId,
    enqueueShopLambdaSizeByExternalId,
  } = useMemo(() => {
    const hotelNames = new Map<string, string>();
    const hotelIdByExternalId = new Map<string, string>();
    const shopLambdaSizeByExternalId = new Map<string, 'NORMAL' | 'MEDIUM' | 'LARGE'>();

    (enqueueHotelsQueryResponse?.results ?? []).forEach((hotel) => {
      if (!hotel || typeof hotel.id !== 'string' || typeof hotel.name !== 'string') {
        return;
      }

      hotelNames.set(hotel.id, hotel.name);
    });

    (enqueueRateScrapeMetadataQueryResponse?.results ?? []).forEach((metadata) => {
      const hotelId = metadata?.hotelId;
      const externalId = metadata.externalId
        ? `${metadata.externalId.site}:${metadata.externalId.id}`
        : undefined;

      if (typeof hotelId === 'string' && externalId) {
        hotelIdByExternalId.set(externalId, hotelId);
      }

      if (externalId && metadata.shopLambdaSize) {
        shopLambdaSizeByExternalId.set(externalId, metadata.shopLambdaSize);
      }
    });

    return {
      enqueueHotelNameByHotelId: hotelNames,
      enqueueHotelIdByExternalId: hotelIdByExternalId,
      enqueueShopLambdaSizeByExternalId: shopLambdaSizeByExternalId,
    };
  }, [enqueueHotelsQueryResponse, enqueueRateScrapeMetadataQueryResponse]);

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Admin Workspace</h1>
        <p className="mt-2 text-sm text-slate-600">
          Hello{data?.name ? `, ${data.name}` : ''}. Select a tool from the top navigation.
        </p>

        <div className="mt-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Links</h2>
          <ul className="mt-3 list-inside list-disc space-y-2 text-sm text-slate-700">
            {dashboardLinks.map((link) => (
              <li key={link.href}>
                <a
                  className="text-sky-700 underline decoration-sky-300 underline-offset-2 transition hover:text-sky-900"
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div
          className="mb-3 mt-3 flex items-center gap-2 border-b border-slate-200 pb-2"
          role="tablist"
          aria-label="Dashboard tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'ACTIVE_HOTELS'}
            aria-controls="dashboard-tabpanel-active-hotels"
            id="dashboard-tab-active-hotels"
            onClick={() => {
              handleTabChange('ACTIVE_HOTELS');
            }}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              activeTab === 'ACTIVE_HOTELS'
                ? 'bg-sky-100 text-sky-800'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Active Hotels
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'RATE_SHOPS'}
            aria-controls="dashboard-tabpanel-rate-shops"
            id="dashboard-tab-rate-shops"
            onClick={() => {
              handleTabChange('RATE_SHOPS');
            }}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              activeTab === 'RATE_SHOPS'
                ? 'bg-sky-100 text-sky-800'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Rate Shops
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'LEARN_RATE_SHOPS'}
            aria-controls="dashboard-tabpanel-learn-rate-shops"
            id="dashboard-tab-learn-rate-shops"
            onClick={() => {
              handleTabChange('LEARN_RATE_SHOPS');
            }}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              activeTab === 'LEARN_RATE_SHOPS'
                ? 'bg-sky-100 text-sky-800'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Learn Rate Shops
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'ENQUEUES'}
            aria-controls="dashboard-tabpanel-enqueues"
            id="dashboard-tab-enqueues"
            onClick={() => {
              handleTabChange('ENQUEUES');
            }}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              activeTab === 'ENQUEUES'
                ? 'bg-sky-100 text-sky-800'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Enqueues
          </button>
        </div>

        {activeTab === 'ACTIVE_HOTELS' ? (
          <div
            role="tabpanel"
            id="dashboard-tabpanel-active-hotels"
            aria-labelledby="dashboard-tab-active-hotels"
          >
            <h2 className="mt-4 text-sm font-semibold uppercase tracking-wide text-slate-700">
              Active Hotels
            </h2>

            <div className="mt-3 rounded-lg border border-slate-200">
              <PaginatedTable
                columns={activeHotelsColumns}
                rows={activeHotelsTableRows}
                getRowKey={(row, index) =>
                  row.hotelSettings.id
                    ? `${row.hotelSettings.id}-${row.site ?? 'no-site'}`
                    : `active-hotel-${index}`
                }
                isLoading={isActiveHotelsPending}
                isError={isActiveHotelsError}
                error={activeHotelsError}
                loadingText="Loading active hotels..."
                errorText="Unable to load active hotels."
                emptyText="No active hotels found."
                rowClassName="hover:bg-sky-50"
                page={activeHotelsQueryResponse?.page ?? activeHotelsPage}
                hasNextPage={activeHotelsHasNextPage}
                isFetchingPage={isActiveHotelsFetching}
                onPreviousPage={() => {
                  setActiveHotelsPage((previousPage) => Math.max(1, previousPage - 1));
                }}
                onNextPage={() => {
                  setActiveHotelsPage(
                    activeHotelsHasNextPage
                      ? (activeHotelsQueryResponse?.nextPage as number)
                      : activeHotelsPage
                  );
                }}
                pageSize={activeHotelsPageSize}
                pageSizeOptions={ACTIVE_HOTELS_PAGE_SIZE_OPTIONS}
                onPageSizeChange={(nextPageSize) => {
                  setActiveHotelsPageSize(nextPageSize);
                  setActiveHotelsPage(1);
                }}
              />
            </div>
          </div>
        ) : activeTab === 'RATE_SHOPS' ? (
          <div
            role="tabpanel"
            id="dashboard-tabpanel-rate-shops"
            aria-labelledby="dashboard-tab-rate-shops"
          >
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                  Rate Shops
                </h2>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                  <div className="h-[25px] w-[25px] shrink-0 rounded-md border border-slate-300 bg-[beige]" />
                  <span>Is Dry Run</span>
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:ml-auto sm:flex-row sm:items-end sm:justify-end">
                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Filter Date (Optional)
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => {
                      setSelectedDate(event.target.value);
                    }}
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>

                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Hotel Id
                  <input
                    type="text"
                    value={rateShopsHotelIdFilter}
                    onChange={(event) => {
                      setRateShopsHotelIdFilter(event.target.value);
                    }}
                    placeholder="Filter by hotel id"
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>
              </div>
            </div>

            {isHotelShopStatsPending ? (
              <p className="mt-3 text-sm text-slate-600">Loading hotel rate shops...</p>
            ) : isHotelShopStatsError ? (
              <p className="mt-3 text-sm text-rose-700">Could not load hotel rate shops.</p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Hotel</th>
                      <th className="px-3 py-2 font-semibold">Source Booking Engine</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Total Saved Rates</th>
                      <th className="w-[12rem] px-3 py-2 font-semibold">Context</th>
                      <th className="px-3 py-2 font-semibold">Latest Run</th>
                      <th className="px-3 py-2 font-semibold">Status Count</th>
                      <th className="w-[25rem] px-3 py-2 font-semibold">Most Recent Error</th>
                      <th className="px-3 py-2 font-semibold">Sentry</th>
                    </tr>
                  </thead>
                  <tbody
                    className="divide-y divide-slate-200 bg-white"
                    onMouseLeave={() => {
                      setHoveredHotelId(null);
                    }}
                  >
                    {hotelShopStats?.map((row) => {
                      const isDryRun = row.dryRun === '1';

                      return (
                        <tr
                          key={`${row['event.externalId']}-${row.status}-${row.latestRun}`}
                          className={`transition-colors ${
                            selectedHotelId === row['event.externalId']
                              ? 'bg-sky-200/70'
                              : (selectedHotelId ?? hoveredHotelId) === row['event.externalId']
                                ? 'bg-sky-100/70'
                                : (selectedHotelId ?? hoveredHotelId)
                                  ? `${isDryRun ? 'bg-[beige]' : 'odd:bg-white even:bg-slate-50/70'} opacity-60`
                                  : isDryRun
                                    ? 'bg-[beige] hover:bg-sky-50'
                                    : 'odd:bg-white even:bg-slate-50/70 hover:bg-sky-50'
                          }`}
                          onMouseEnter={() => {
                            if (!selectedHotelId) {
                              setHoveredHotelId(row['event.externalId']);
                            }
                          }}
                          onClick={() => {
                            setSelectedHotelId((previous) =>
                              previous === row['event.externalId'] ? null : row['event.externalId']
                            );
                          }}
                        >
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {(() => {
                              const hotelExternalId = row['event.externalId'];

                              if (!hotelExternalId) {
                                return null;
                              }

                              const hotelId = hotelIdByExternalId.get(hotelExternalId);
                              const hotelName = hotelId
                                ? hotelByHotelId.get(hotelId)?.name
                                : undefined;
                              const site = hotelExternalId.split(':')[0];
                              const hotelSettings = hotelId
                                ? hotelSettingsByHotelId.get(hotelId)
                                : undefined;
                              const metadata = rateScrapeMetadataByExternalId.get(hotelExternalId);

                              return renderHotelLink({
                                hotelName,
                                primaryId: hotelExternalId,
                                linkHotelId: hotelId ?? hotelExternalId,
                                isBootstrapLearner: hotelSettings?.optionsTuples?.some(
                                  (option) =>
                                    option.option === 'BOOTSTRAP_LEARNING' && option.site === site
                                ),
                                isSetsLearning: hotelSettings?.optionsTuples?.some(
                                  (option) =>
                                    option.option === 'SETS_LEARNING' && option.site === site
                                ),
                                isRemoteCdpHeaded: isRemoteCdpHeadedLearnExtra(
                                  metadata?.learnExtra
                                ),
                                shopLambdaSize: metadata?.shopLambdaSize,
                              });
                            })()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {(() => {
                              const hotelExternalId = row['event.externalId'];
                              return (
                                rateScrapeMetadataByExternalId.get(hotelExternalId)
                                  ?.sourceBookingEngine ?? '-'
                              );
                            })()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">{row.status}</td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {row.totalSavedRates}
                            {row.totalSoldOut ? ` (Sold out: ${row.totalSoldOut})` : ''}
                          </td>
                          <td className="w-[12rem] max-w-[12rem] break-words px-3 py-2 align-top">
                            {row.primaryContext || row.additionalContext}
                          </td>
                          <td
                            className="whitespace-nowrap px-3 py-2 align-top"
                            title={row.latestRun}
                          >
                            {formatLatestRunInBrowserLocale(row.latestRun)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {row.statusCount}
                          </td>
                          <td
                            className="w-[25rem] max-w-[25rem] px-3 py-2 align-top"
                            title={row.mostRecentError}
                          >
                            <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                              {row.mostRecentError}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            <a
                              className="text-sky-700 underline decoration-sky-300 underline-offset-2 transition hover:text-sky-900"
                              href={buildHotelSentryLogsLink(row['event.externalId'], selectedDate)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === 'LEARN_RATE_SHOPS' ? (
          <div
            role="tabpanel"
            id="dashboard-tabpanel-learn-rate-shops"
            aria-labelledby="dashboard-tab-learn-rate-shops"
          >
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                  Learn Rate Shops
                </h2>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                  <div className="h-[25px] w-[25px] shrink-0 rounded-md border border-slate-300 bg-[beige]" />
                  <span>Is Dry Run</span>
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:ml-auto sm:flex-row sm:items-end sm:justify-end">
                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Filter Date (Optional)
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => {
                      setSelectedDate(event.target.value);
                    }}
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>

                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Hotel Id
                  <input
                    type="text"
                    value={learnRateShopsHotelIdFilter}
                    onChange={(event) => {
                      setLearnRateShopsHotelIdFilter(event.target.value);
                    }}
                    placeholder="Filter by hotel id"
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>
              </div>
            </div>

            {isHotelLearningStatsPending ? (
              <p className="mt-3 text-sm text-slate-600">Loading learn rate shops...</p>
            ) : isHotelLearningStatsError ? (
              <p className="mt-3 text-sm text-rose-700">Could not load learn rate shops.</p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Timestamp</th>
                      <th className="px-3 py-2 font-semibold">Hotel</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Result</th>
                      <th className="px-3 py-2 font-semibold">Trace</th>
                    </tr>
                  </thead>
                  <tbody
                    className="divide-y divide-slate-200 bg-white"
                    onMouseLeave={() => {
                      setHoveredHotelId(null);
                    }}
                  >
                    {hotelLearningStats?.map((row, index) => {
                      const isDryRun = row.dryRun === '1';
                      const hotelExternalId = row['event.externalId'];

                      return (
                        <tr
                          key={`${hotelExternalId}-${row.status}-${row['@timestamp'] ?? index}`}
                          className={`transition-colors ${
                            selectedHotelId === hotelExternalId
                              ? 'bg-sky-200/70'
                              : (selectedHotelId ?? hoveredHotelId) === hotelExternalId
                                ? 'bg-sky-100/70'
                                : (selectedHotelId ?? hoveredHotelId)
                                  ? `${isDryRun ? 'bg-[beige]' : 'odd:bg-white even:bg-slate-50/70'} opacity-60`
                                  : isDryRun
                                    ? 'bg-[beige] hover:bg-sky-50'
                                    : 'odd:bg-white even:bg-slate-50/70 hover:bg-sky-50'
                          }`}
                          onMouseEnter={() => {
                            if (!selectedHotelId) {
                              setHoveredHotelId(hotelExternalId);
                            }
                          }}
                          onClick={() => {
                            setSelectedHotelId((previous) =>
                              previous === hotelExternalId ? null : hotelExternalId
                            );
                          }}
                        >
                          <td
                            className="whitespace-nowrap px-3 py-2 align-top"
                            title={row['@timestamp'] ?? ''}
                          >
                            {row['@timestamp']
                              ? formatLatestRunInBrowserLocale(row['@timestamp'])
                              : '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {(() => {
                              const hotelId =
                                learnRateShopsHotelIdByExternalId.get(hotelExternalId);
                              const hotelName = hotelId
                                ? learnRateShopsHotelByHotelId.get(hotelId)?.name
                                : undefined;
                              const site = hotelExternalId.split(':')[0];
                              const hotelSettings = hotelId
                                ? learnRateShopsHotelSettingsByHotelId.get(hotelId)
                                : undefined;
                              const metadata =
                                learnRateShopsRateScrapeMetadataByExternalId.get(hotelExternalId);

                              return renderHotelLink({
                                hotelName,
                                primaryId: hotelExternalId,
                                linkHotelId: hotelId ?? hotelExternalId,
                                isBootstrapLearner: hotelSettings?.optionsTuples?.some(
                                  (option) =>
                                    option.option === 'BOOTSTRAP_LEARNING' && option.site === site
                                ),
                                isRemoteCdpHeaded: isRemoteCdpHeadedLearnExtra(
                                  metadata?.learnExtra
                                ),
                                shopLambdaSize: metadata?.shopLambdaSize,
                              });
                            })()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">{row.status}</td>
                          <td className="px-3 py-2 align-top">{renderLearnRateShopResult(row)}</td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {row.trace ? (
                              <a
                                className="text-sky-700 underline decoration-sky-300 underline-offset-2 transition hover:text-sky-900"
                                href={`/tools/trace-viewer?traceId=${encodeURIComponent(row.trace)}`}
                              >
                                View
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div
            className="mt-4"
            role="tabpanel"
            id="dashboard-tabpanel-enqueues"
            aria-labelledby="dashboard-tab-enqueues"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                Enqueues
              </h2>
              <div className="flex flex-col gap-3 sm:ml-auto sm:flex-row sm:items-end sm:justify-end">
                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Filter Date (Optional)
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => {
                      setSelectedDate(event.target.value);
                    }}
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>

                <MultiSelectCombobox
                  label="Enqueue Type"
                  options={ENQUEUE_TYPE_OPTIONS}
                  selectedValues={selectedEnqueueTypes}
                  allSelectedLabel="All enqueue types"
                  onSelectedValuesChange={(values: ReadonlyArray<EnqueueType>) => {
                    setSelectedEnqueueTypes([...values]);
                  }}
                  containerClassName="shrink-0 sm:min-w-[16rem] flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700"
                />

                <MultiSelectCombobox
                  label="Job Type"
                  options={JOB_TYPE_OPTIONS}
                  selectedValues={selectedJobTypes}
                  allSelectedLabel="All job types"
                  onSelectedValuesChange={(values: ReadonlyArray<JobType>) => {
                    setSelectedJobTypes([...values]);
                  }}
                  containerClassName="shrink-0 sm:min-w-[14rem] flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700"
                />

                <label className="flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Hotel Id
                  <input
                    type="text"
                    value={enqueueHotelIdFilter}
                    onChange={(event) => {
                      setEnqueueHotelIdFilter(event.target.value);
                    }}
                    placeholder="Filter by hotel id"
                    className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
                  />
                </label>
              </div>
            </div>

            {isEnqueueStatsPending ? (
              <p className="mt-3 text-sm text-slate-600">Loading enqueues...</p>
            ) : isEnqueueStatsError ? (
              <p className="mt-3 text-sm text-rose-700">Could not load enqueues.</p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="whitespace-nowrap px-3 py-2 font-semibold">Timestamp</th>
                      <th className="whitespace-nowrap px-3 py-2 font-semibold">Enqueue Type</th>
                      <th className="whitespace-nowrap px-3 py-2 font-semibold">Job Type</th>
                      <th className="px-3 py-2 font-semibold">Hotel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {(enqueueStats ?? []).map((row, index) => {
                      const externalIds = parseRawEnqueueHotelExternalIds(row.rawHotels);

                      return (
                        <tr
                          key={`${row['@timestamp'] ?? 'timestamp'}-${row.enqueueType ?? 'enqueueType'}-${row.jobType ?? 'jobType'}-${index}`}
                        >
                          <td
                            className="whitespace-nowrap px-3 py-2 align-top"
                            title={row['@timestamp'] ?? ''}
                          >
                            {row['@timestamp']
                              ? formatLatestRunInBrowserLocale(row['@timestamp'])
                              : '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {row.enqueueType ?? '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {row.jobType ?? '-'}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {externalIds.length > 0 ? (
                              <ul className="space-y-1">
                                {externalIds.map((externalId) => {
                                  const hotelId = enqueueHotelIdByExternalId.get(externalId);
                                  const hotelName = hotelId
                                    ? enqueueHotelNameByHotelId.get(hotelId)
                                    : undefined;

                                  return (
                                    <li key={externalId} className="whitespace-nowrap">
                                      {renderHotelLink({
                                        hotelName,
                                        primaryId: externalId,
                                        linkHotelId: hotelId ?? externalId,
                                        shopLambdaSize:
                                          enqueueShopLambdaSizeByExternalId.get(externalId),
                                      })}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}
